import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dataService } from '../services/dataService';

const LAST_SYNC_PREFIX = 'lastSync:';

export function useOfflineCollection({
  entityType,
  fetcher,
  orgId,
  accountId,
  enabled = true,
  staleTime = 5 * 60 * 1000,
  hydrateOnMount = true
}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hydrating, setHydrating] = useState(false);
  const [error, setError] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const subscriptionRef = useRef(null);
  const missingDataFetchRef = useRef(false);
  const fetcherRef = useRef(fetcher);

  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  const metaKey = useMemo(() => {
    return `${LAST_SYNC_PREFIX}${entityType}${orgId ? `:${orgId}` : ''}`;
  }, [entityType, orgId]);

  const loadLocal = useCallback(async () => {
    if (!enabled || !entityType) return;
    try {
      await dataService.ensureInitialized();
      const localData = await dataService.getEntities(entityType, { orgId, accountId });
      setData(Array.isArray(localData) ? localData : []);
      setLoading(false);
      const meta = await dataService.getMetadata(metaKey);
      setLastSync(meta || null);
    } catch (err) {
      console.error('Failed to load local data', entityType, err);
      setError(err);
      setLoading(false);
    }
  }, [entityType, orgId, accountId, enabled, metaKey]);

  const hydrate = useCallback(async ({ force = false } = {}) => {
    const activeFetcher = fetcherRef.current;
    if (!enabled || typeof activeFetcher !== 'function') return { skipped: true };

    try {
      setHydrating(true);
      const result = await dataService.hydrateCollection(entityType, activeFetcher, {
        orgId,
        accountId,
        force,
        staleTime
      });
      const meta = await dataService.getMetadata(metaKey);
      setLastSync(meta || null);
      return result;
    } catch (err) {
      console.error('Failed to hydrate collection', entityType, err);
      setError(err);
      return { error: err };
    } finally {
      setHydrating(false);
    }
  }, [enabled, entityType, orgId, accountId, staleTime, metaKey]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    loadLocal();

    if (subscriptionRef.current) {
      subscriptionRef.current();
      subscriptionRef.current = null;
    }

    dataService.ensureInitialized()
      .then(() => {
        subscriptionRef.current = dataService.subscribe(entityType, (records) => {
          setData(Array.isArray(records) ? records : []);
        }, { orgId, accountId });
      })
      .catch((err) => {
        console.error('Failed to attach subscription', entityType, err);
        setError(err);
      });

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current();
        subscriptionRef.current = null;
      }
    };
  }, [entityType, orgId, accountId, enabled, loadLocal]);

  useEffect(() => {
    if (!enabled || !hydrateOnMount) return;
    if (!navigator.onLine) {
      setHydrating(false);
      return;
    }

    hydrate();
  }, [enabled, hydrateOnMount, hydrate]);

  useEffect(() => {
    if (!enabled) return;
    if (hydrating) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      missingDataFetchRef.current = false;
      return;
    }
    if (data.length > 0) {
      missingDataFetchRef.current = false;
      return;
    }
    if (missingDataFetchRef.current) return;

    missingDataFetchRef.current = true;
    hydrate({ force: true }).finally(() => {
      missingDataFetchRef.current = false;
    });
  }, [data, enabled, hydrate, hydrating]);

  return {
    data,
    loading: loading || (hydrating && data.length === 0),
    hydrating,
    error,
    lastSync,
    refresh: hydrate,
    isOffline: !navigator.onLine
  };
}


